/**
 * Forwards v2 API client. One function per backend route (plan Appendix C), same order.
 *
 * Every `/api/forwards` route is `@access(AccessClass.USER)`; ownership is enforced in the services,
 * so a mandate the caller may not see answers 404 rather than 403 and the UI must treat both the same.
 * The `/api/admin/forwards` routes are staff-only: every write is `SUPER`, and every read —
 * the picker's and the pay-as-produced wizard's included — is `SALES_READ`, so a sales admin opens
 * those pages read-only (user decision, 2026-09-28).
 */

import { goSolidApi } from "@/composables/useGoSolidApi";
import { apiErrorResponse } from "@/utils/apiErrors";
import type { PeriodWidth } from "@/forwards2/periodUtils";
import type {
  ActiveMandateRow,
  AdminMandateListResponse,
  DealIdentity,
  DealsQuery,
  DealsTableResponse,
  ForwardYearTotalsResponse,
  OverviewResponse,
  PapDealCreateRequest,
  PapDealCreateResult,
  PapWizardContext,
  PickerConfirmRequest,
  PickerMandates,
  PickerQuery,
  MandateCommitmentRow,
  MandateDetailResponse,
  MandateDeviceRow,
  MandateListRow,
  MandateMemberRow,
  MandatePreviewResponse,
  MandateWriteRequest,
  MandateWriteResult,
  TimelineQuery,
  TimelineResponse,
  UpcomingDeliveriesQuery,
  UpcomingDeliveriesResponse,
  AddCommitmentResult,
  CommitmentMandateRow,
  CriteriaUpdateResult,
  DealMetadataRequest,
  DealResponse,
  EligibilityCriteriaRequest,
  EscrowAttributionRow,
  EscrowMoveResult,
  EscrowPullRequest,
  EscrowReturnRequest,
  MoveSliceResult,
  NarrowSliceResult,
  OfferingSheetResult,
  PapMonitoringResponse,
  PapScheduleUpdateRequest,
  PlanApplyResult,
  PlanStateRequest,
  ProducerPricesRequest,
  MandatesAttentionResponse,
  DeliveryNotesRequest,
  ResizeCommitmentResult,
} from "@/forwards2/forwardsTypes";
import type { FlattenedPortfolioItem } from "@/types";

const MANDATES = "/api/forwards/mandates";
const OVERVIEW = "/api/admin/forwards/overview";
const DEALS = "/api/admin/forwards/deals";
const UPCOMING_DELIVERIES = "/api/admin/forwards/upcoming-deliveries";
const YEAR_TOTALS = "/api/admin/forwards/year-totals";
const ADMIN = "/api/admin/forwards";

export const forwardsApi = {
  /** Plain list of the scope's mandates, archived ones included — for mandate selects. */
  listMandates: () => goSolidApi.get<MandateListRow[]>(MANDATES),

  /**
   * The active mandates any of these companies is a member of, each once — the producer dashboard's
   * list, including mandates the session may not open whole.
   *
   * Sent as `company_ids[]=1&company_ids[]=2`. A company the session may not read answers 404.
   */
  listActiveMandates: (companyIds: number[]) =>
    goSolidApi.get<ActiveMandateRow[]>(`${MANDATES}/active`, {
      params: { company_ids: companyIds },
    }),

  /**
   * The mandates panel's timeline: aggregate band plus one row per mandate over one calendar year's quarters.
   *
   * Axios appends `[]` to array parameters, which is the shape the route expects
   * (`status[]=active&company_ids[]=1`).
   */
  getMandateTimeline: (query: TimelineQuery) =>
    goSolidApi.get<TimelineResponse>(`${MANDATES}/timeline`, {
      params: {
        year: query.year,
        status: query.status,
        company_ids: query.company_ids,
      },
    }),

  /**
   * The mandates panel's Needs attention: what the account is asked to change about its mandates,
   * narrowed by the timeline's company filter (`company_ids[]`, none for every mandate in scope) but
   * not by its year or status.
   */
  getMandatesAttention: (companyIds: number[]) =>
    goSolidApi.get<MandatesAttentionResponse>(`${MANDATES}/needs-attention`, {
      params: { company_ids: companyIds },
    }),

  /** Company choices scoped by the session, including disabled forward-sales companies. */
  listCandidateCompanies: () =>
    goSolidApi.get<MandateMemberRow[]>(`${MANDATES}/companies`),

  createMandate: (body: MandateWriteRequest) =>
    goSolidApi.post<MandateWriteResult>(MANDATES, body),

  getMandate: (mandateId: number) =>
    goSolidApi.get<MandateDetailResponse>(`${MANDATES}/${mandateId}`),

  /** Replaces the mandate wholesale — always send the complete form. `force` is admin-only. */
  updateMandate: (
    mandateId: number,
    body: MandateWriteRequest,
    options: { force?: boolean } = {}
  ) =>
    goSolidApi.put<MandateWriteResult>(`${MANDATES}/${mandateId}`, body, {
      params: options.force ? { force: 1 } : {},
    }),

  deleteMandate: (mandateId: number) =>
    goSolidApi.delete<void>(`${MANDATES}/${mandateId}`),

  /** Live feedback for the unsaved form; `mandateId` prices it against an existing mandate's commitments. */
  previewMandate: (body: MandateWriteRequest, mandateId?: number | null) =>
    goSolidApi.post<MandatePreviewResponse>(`${MANDATES}/preview`, body, {
      params: mandateId ? { mandate_id: mandateId } : {},
    }),

  archiveMandate: (mandateId: number) =>
    goSolidApi.post<{ mandate_id: number; archived_at: string | null }>(
      `${MANDATES}/${mandateId}/archive`
    ),

  unarchiveMandate: (mandateId: number) =>
    goSolidApi.post<{ mandate_id: number; archived_at: null }>(
      `${MANDATES}/${mandateId}/unarchive`
    ),

  listMandateCommitments: (mandateId: number) =>
    goSolidApi.get<MandateCommitmentRow[]>(
      `${MANDATES}/${mandateId}/commitments`
    ),

  listMandateDevices: (mandateId: number) =>
    goSolidApi.get<MandateDeviceRow[]>(`${MANDATES}/${mandateId}/devices`),

  /**
   * The devices a mandate over these companies could sell from, before the mandate exists.
   *
   * The same rows as `listMandateDevices`, with `in_mandate` always false and the estimates covering this
   * year and the next by quarter — what the editor's allowlist panel needs on its first save.
   * Answers 404 unless the caller has access to every company asked about.
   */
  listCandidateDevices: (companyIds: number[]) =>
    goSolidApi.get<MandateDeviceRow[]>(`${MANDATES}/devices`, {
      params: { company_ids: companyIds },
    }),

  /** The overview's Needs attention rows, across every account. */
  getOverview: () => goSolidApi.get<OverviewResponse>(OVERVIEW),

  /** Per production year, the forward volume sold and its average buyer price, oldest year first. */
  getYearTotals: () => goSolidApi.get<ForwardYearTotalsResponse>(YEAR_TOTALS),

  /**
   * The managed deals' deliveries, filtered, nearest window end first.
   *
   * An empty `transactionStatus` list, an open window end and an empty `dealName` are dropped
   * rather than sent, so the endpoint applies its not-completed default and leaves the rest
   * unbounded.
   * Bounds out of order answer 400.
   */
  listUpcomingDeliveries: (query: UpcomingDeliveriesQuery) =>
    goSolidApi.get<UpcomingDeliveriesResponse>(UPCOMING_DELIVERIES, {
      params: {
        ...(query.transactionStatus.length > 0
          ? { transaction_status: query.transactionStatus }
          : {}),
        ...(query.windowEndFrom
          ? { window_end_from: query.windowEndFrom }
          : {}),
        ...(query.windowEndTo ? { window_end_to: query.windowEndTo } : {}),
        ...(query.dealName ? { deal_name: query.dealName } : {}),
      },
    }),

  /**
   * The deliveries spreadsheet for accounting: every delivery the card can list, ignoring its filters,
   * with the buyer, volume, buyer price, value and buyer invoice number. The server names the file.
   */
  downloadUpcomingDeliveries: () =>
    goSolidApi.get<Blob>(`${UPCOMING_DELIVERIES}/xlsx`, {
      responseType: "blob",
    }),

  /**
   * The deals the caller manages, filtered.
   *
   * An empty `name`/`mandate`/`company` is dropped rather than sent as a blank substring match, and
   * an empty `status` list is dropped so the endpoint applies its own draft+confirmed default.
   */
  listDeals: (query: DealsQuery) =>
    goSolidApi.get<DealsTableResponse>(DEALS, {
      params: {
        ...(query.status.length > 0 ? { status: query.status } : {}),
        ...(query.name ? { name: query.name } : {}),
        ...(query.mandate ? { mandate: query.mandate } : {}),
        ...(query.company ? { company: query.company } : {}),
      },
    }),

  /**
   * The volume picker's take table: which mandates can serve this product over this range.
   *
   * Axios appends `[]` to array parameters, which is the shape the route reads
   * (`periods[]=2026-Q1&countries[]=EE`). An unset filter is dropped rather than sent empty: the
   * backend reads `?support=` as "unrestricted", but a `null` in the query string would not
   * survive the round trip as one.
   */
  getPickerMandates: (query: PickerQuery) =>
    goSolidApi.get<PickerMandates>(`${ADMIN}/picker/mandates`, {
      params: {
        periods: query.periods,
        countries: query.criteria.countries,
        technologies: query.criteria.technologies,
        ecolabels: query.criteria.ecolabels,
        ...(query.criteria.cod_from
          ? { cod_from: query.criteria.cod_from }
          : {}),
        ...(query.criteria.cod_until
          ? { cod_until: query.criteria.cod_until }
          : {}),
        ...(query.criteria.support === null
          ? {}
          : { support: query.criteria.support }),
        ...(query.min_price_filter
          ? { min_price_filter: query.min_price_filter }
          : {}),
        taken_mandate_ids: query.taken_mandate_ids,
      },
    }),

  /**
   * Confirms the picker: a draft deal, one commitment per mandate and the scaffolded delivery plan.
   *
   * A take beyond a mandate's sales strategy is refused (400); the picker has no override.
   */
  createDeal: (body: PickerConfirmRequest) =>
    goSolidApi.post<DealIdentity>(`${ADMIN}/deals`, body),

  /** Creates a pay-as-produced deal from one PAP mandate and its nominated monthly schedule. */
  createPapDeal: (body: PapDealCreateRequest) =>
    goSolidApi.post<PapDealCreateResult>(
      `${ADMIN}/pay-as-produced-deals`,
      body
    ),

  /** The wizard's owner field and which mandates can take a contract, plus the chosen mandate's advisories once there is one. Staff read (a sales admin too). */
  getPapWizardContext: (mandateId: number | null) =>
    goSolidApi.get<PapWizardContext>(`${ADMIN}/pay-as-produced-deals/context`, {
      params: mandateId === null ? {} : { mandate_id: mandateId },
    }),

  /**
   * Staff read (a sales admin too): every mandate on the platform, for the surfaces that are not bound
   * to one account's scope — the pay-as-produced wizard's mandate select.
   *
   * Archived mandates are left out unless asked for, and `isPap` narrows to (or away from) the
   * pay-as-produced ones, so the caller filters nothing itself.
   */
  listAdminMandates: (
    options: { isPap?: boolean; includeArchived?: boolean } = {}
  ) =>
    goSolidApi.get<AdminMandateListResponse>(`${ADMIN}/mandates`, {
      params: {
        ...(options.isPap === undefined ? {} : { is_pap: options.isPap }),
        ...(options.includeArchived ? { include_archived: 1 } : {}),
      },
    }),

  // --- deal page (WP-09b) ---------------------------------------------------------------------
  //
  // Every mutating deal call answers with the whole `DealResponse` (some wrapped alongside what the
  // operation returned), so the deal page replaces its state from the response and never re-fetches.

  /** The deal page in one request. A deal the caller may not see answers 404, like a missing one. */
  getDeal: (dealId: number) =>
    goSolidApi.get<DealResponse>(`${DEALS}/${dealId}`),

  /** Buyer and contract details. Only the fields present in `body` are written. */
  updateDealMetadata: (dealId: number, body: DealMetadataRequest) =>
    goSolidApi.patch<DealResponse>(`${DEALS}/${dealId}`, body),

  /**
   * Replaces the deal's product definition. A 400 refuses a change that conflicts with backing or
   * escrow the deal still delivers; `dryRun` lists those conflicts and the warnings without writing.
   */
  updateDealCriteria: (
    dealId: number,
    body: EligibilityCriteriaRequest,
    options: { dryRun?: boolean } = {}
  ) =>
    goSolidApi.put<CriteriaUpdateResult>(`${DEALS}/${dealId}/criteria`, body, {
      params: options.dryRun ? { dry_run: 1 } : {},
    }),

  confirmDeal: (dealId: number) =>
    goSolidApi.post<DealResponse>(`${DEALS}/${dealId}/confirm`),

  cancelDeal: (dealId: number) =>
    goSolidApi.post<DealResponse>(`${DEALS}/${dealId}/cancel`),

  /** `SUPER` only: the confirmed deal leaves the allocation queue; confirming again queues it anew. */
  unconfirmDeal: (dealId: number) =>
    goSolidApi.post<DealResponse>(`${ADMIN}/deals/${dealId}/unconfirm`),

  /**
   * The plan the deal should end up with, applied all-or-nothing.
   *
   * `state.plan_version` is the stamp the screen was composed against: a 409 means the plan moved on
   * and the deal has to be reloaded. A delivery of the deal the state leaves out is removed.
   * `dryRun` returns the same payload without writing, which is what fills the editor's warning
   * banners and its per-card change lines.
   */
  savePlan: (
    dealId: number,
    state: PlanStateRequest,
    options: { dryRun?: boolean } = {}
  ) =>
    goSolidApi.put<PlanApplyResult>(`${DEALS}/${dealId}/plan`, state, {
      params: options.dryRun ? { dry_run: 1 } : {},
    }),

  /** Moves a slice to another delivery of the same deal; a volume splits part of it instead. */
  moveSlice: (
    dealId: number,
    sliceId: number,
    body: { target_delivery_id: number; volume_mwh: number | null }
  ) =>
    goSolidApi.post<MoveSliceResult>(
      `${DEALS}/${dealId}/slices/${sliceId}/move`,
      body
    ),

  /** Breaks a slice's period into sub-periods of `width`, re-pinning its escrow. */
  narrowSlice: (dealId: number, sliceId: number, width: PeriodWidth) =>
    goSolidApi.post<NarrowSliceResult>(
      `${DEALS}/${dealId}/slices/${sliceId}/narrow`,
      { width }
    ),

  /**
   * Resizes a backed slice: up pulls again from the same mandate, down gives the volume back as open.
   *
   * Growing is capped by the mandate's sales strategy; `ignoreSalesStrategy` (super admin only) lifts
   * the cap to availability, and is left out of the body otherwise.
   */
  resizeCommitment: (
    dealId: number,
    sliceId: number,
    volumeMwh: number,
    options: { ignoreSalesStrategy?: boolean } = {}
  ) =>
    goSolidApi.post<ResizeCommitmentResult>(
      `${DEALS}/${dealId}/slices/${sliceId}/resize`,
      {
        volume_mwh: volumeMwh,
        ...(options.ignoreSalesStrategy
          ? { dangerous_ignore_sales_strategy: true }
          : {}),
      }
    ),

  /**
   * Backs some of the deal's open slices from one mandate. `takes` maps open slice id → MWh.
   *
   * Capped by the mandate's sales strategy like the picker, but unlike the picker it takes the
   * override: `dangerous_ignore_sales_strategy`, super admin only and sent only as true.
   */
  addCommitment: (
    dealId: number,
    body: {
      mandate_id: number;
      takes: Record<number, number>;
      dangerous_ignore_sales_strategy?: boolean;
    }
  ) =>
    goSolidApi.post<AddCommitmentResult>(
      `${DEALS}/${dealId}/commitments`,
      body
    ),

  /** Removes a slice's backing; the volume turns open and returns to the mandate's availability. */
  deleteBacking: (dealId: number, sliceId: number) =>
    goSolidApi.delete<DealResponse>(
      `${DEALS}/${dealId}/slices/${sliceId}/backing`
    ),

  /**
   * The add-commitment type-ahead. Ineligible mandates come back too, with every failing criterion.
   *
   * Each row carries both the strategy cap and full availability (`capacity_by_period`), so the
   * override needs no second read. `mandateId` narrows the answer to one mandate, which is how the resize
   * dialog learns its grow cap; it is not sent unless set.
   */
  listCommitmentMandates: (
    dealId: number,
    query: string,
    options: { mandateId?: number } = {}
  ) =>
    goSolidApi.get<CommitmentMandateRow[]>(
      `${DEALS}/${dealId}/commitment-mandates`,
      {
        params: {
          ...(query ? { q: query } : {}),
          ...(options.mandateId === undefined
            ? {}
            : { mandate_id: options.mandateId }),
        },
      }
    ),

  /** Builds the offering sheet from the current plan and appends its URL to the deal's links. */
  generateOfferingSheet: (dealId: number) =>
    goSolidApi.post<OfferingSheetResult>(`${DEALS}/${dealId}/offering-sheet`),

  /**
   * Downloads an offering sheet that was built as a file instead of a Drive spreadsheet — the
   * development-only route the deal's link then points at. The id and filename travel apart so the
   * authenticated client is never aimed at a URL taken whole from stored data.
   *
   * @throws the usual axios error; a 404 means the sheet is gone or this is not a file-backed link.
   */
  downloadOfferingSheet: (dealId: number, filename: string) =>
    goSolidApi.get<Blob>(`${DEALS}/${dealId}/offering-sheets/${filename}`, {
      responseType: "blob",
    }),

  /** Staff read (a sales admin too): every live escrowed certificate of the deal, for the return-to-owner picker. */
  listEscrowAttributions: (dealId: number) =>
    goSolidApi.get<{ rows: EscrowAttributionRow[] }>(
      `${ADMIN}/deals/${dealId}/escrow/attributions`
    ),

  /**
   * Admin only: transfers selected escrow back to its owners.
   *
   * Registry transfers fail per item, so a 200 can still carry failed rows — read `items[].ok`.
   */
  returnEscrow: (dealId: number, body: EscrowReturnRequest) =>
    goSolidApi.post<EscrowMoveResult>(
      `${ADMIN}/deals/${dealId}/escrow/return`,
      body
    ),

  /** Admin only: pulls certificates that flowed past the forwards step into a commitment's escrow. */
  pullEscrow: (dealId: number, body: EscrowPullRequest) =>
    goSolidApi.post<EscrowMoveResult>(
      `${ADMIN}/deals/${dealId}/escrow/pull`,
      body
    ),

  /**
   * Staff read: one company's portfolio items of the given types — what a pull into escrow picks
   * from. The admin item feed (`GET /api/admin/portfolios/flattened-portfolio-items`), whose
   * `portfolio_types[]` parameter is spelled out here because it is not a forwards route.
   */
  listPullableItems: (companyId: number, portfolioTypes: readonly string[]) => {
    const params = new URLSearchParams({ company_id: String(companyId) });
    for (const type of portfolioTypes) params.append("portfolio_types[]", type);
    return goSolidApi.get<{ portfolio_items: FlattenedPortfolioItem[] }>(
      `/api/admin/portfolios/flattened-portfolio-items?${params.toString()}`
    );
  },

  /**
   * Admin only: replaces one delivery's mandate prices and default producer price.
   *
   * The map is the delivery's whole set of mandate prices, so a mandate left out of it follows the
   * default producer price, or the delivery's own price when there is none. The plan is untouched — `plan_version` does not
   * move — and the response is the deal page as usual.
   */
  saveProducerPrices: (
    dealId: number,
    deliveryId: number,
    body: ProducerPricesRequest
  ) =>
    goSolidApi.put<DealResponse>(
      `${ADMIN}/deals/${dealId}/deliveries/${deliveryId}/producer-prices`,
      body
    ),

  /**
   * Admin only: replaces one delivery's staff-only notes; null or blank clears them. They are never
   * copied onto the delivery's transaction. The response is the deal page as usual.
   */
  saveDeliveryNotes: (
    dealId: number,
    deliveryId: number,
    body: DeliveryNotesRequest
  ) =>
    goSolidApi.put<DealResponse>(
      `${ADMIN}/deals/${dealId}/deliveries/${deliveryId}/internal-notes`,
      body
    ),

  /**
   * The pay-as-produced monitoring table (nominated vs escrowed, per month), for whoever may read the
   * deal. A deal the caller may not see answers 404; a fixed-volume deal answers 400.
   */
  getPapMonitoring: (dealId: number) =>
    goSolidApi.get<PapMonitoringResponse>(
      `${DEALS}/${dealId}/pay-as-produced/monitoring`
    ),

  /** Admin only: re-nominates a pay-as-produced schedule, its tolerance, or both. */
  updatePapSchedule: (dealId: number, body: PapScheduleUpdateRequest) =>
    goSolidApi.patch<DealResponse>(
      `${ADMIN}/deals/${dealId}/pay-as-produced`,
      body
    ),
};

/**
 * The user-facing sentence a forwards2 domain error carries.
 *
 * Every `ForwardsError` reaches the global handler with a stable `error_type` and the message in
 * `message` — safe to show, unlike the generic `error` field which is only the correlation id.
 *
 * @returns the message, or null when the failure was not a domain error (a network drop, a 500).
 */
export function forwardsErrorMessage(error: unknown): string | null {
  if (!isDomainError(error)) return null;

  const message = apiErrorResponse(error)?.data?.message;
  return typeof message === "string" && message.length > 0 ? message : null;
}

/** `error_type` of a tagged domain error, or null. */
export function forwardsErrorType(error: unknown): string | null {
  if (!isDomainError(error)) return null;

  const errorType = apiErrorResponse(error)?.data?.error_type;
  return typeof errorType === "string" && errorType.length > 0
    ? errorType
    : null;
}

/** The backend tags a refused mandate save that an admin may force past with this `error_type` (DSP-03). */
export const FEASIBILITY_BLOCK_ERROR_TYPE = "FORWARDS_FEASIBILITY_BLOCK";
export const MANDATE_VERSION_CONFLICT_ERROR_TYPE =
  "FORWARDS_MANDATE_VERSION_CONFLICT";
const FORWARDS_ERROR_TYPE = "FORWARDS_ERROR";

/**
 * Whether the backend answered with a domain error the UI is expected to render itself.
 *
 * Anything else (no response, a 5xx) is a bug or an outage and belongs with `globalErrorHandler`.
 */
export function isDomainError(error: unknown): boolean {
  const response = apiErrorResponse(error);
  if (response === undefined) return false;
  const { status } = response;
  const errorType = response.data?.error_type;
  return (
    status >= 400 &&
    status < 500 &&
    (errorType === FORWARDS_ERROR_TYPE ||
      errorType === FEASIBILITY_BLOCK_ERROR_TYPE ||
      errorType === MANDATE_VERSION_CONFLICT_ERROR_TYPE)
  );
}

/**
 * The deal's `plan_version` now, when a save was refused for carrying an older one: someone else
 * saved meanwhile, and the editor merges its draft onto the latest deal rather than showing the
 * refusal (`PlanVersionConflictError`, 409, `error_data.current_version`).
 *
 * @returns the current version, or null for any other outcome.
 */
export function planVersionConflict(error: unknown): number | null {
  const response = apiErrorResponse(error);
  const current = response?.data?.error_data?.current_version;
  return response?.status === 409 && typeof current === "number"
    ? current
    : null;
}

/** True when the resource is missing, or the caller may not see it — the API cannot distinguish them. */
export function isNotFoundError(error: unknown): boolean {
  return apiErrorResponse(error)?.status === 404;
}
